/**
 * Resolves relative markdown links and opens targets via the main tab system.
 * Provider lives in ResizableLayout; panels use useInternalMarkdownLinkClick(basePath?).
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react"
import type { MouseEvent as ReactMouseEvent } from "react"
import { resolveDocumentLink } from "@/lib/api"
import { hrefForInAppDocumentNavigation } from "@/lib/maater-document-url"

export type InternalMarkdownLinkOpener = (
  href: string,
  baseFilePath: string | undefined,
  event?: MouseEvent
) => Promise<void>

const InternalMarkdownLinkContext =
  createContext<InternalMarkdownLinkOpener | null>(null)

export function InternalMarkdownLinkProvider({
  children,
  navigateToDocument,
  getActiveTabBasePath,
}: {
  children: ReactNode
  navigateToDocument: (
    targetPathOrId: string,
    event?: ReactMouseEvent
  ) => void | Promise<void>
  getActiveTabBasePath: () => string | undefined
}) {
  const openInternalMarkdownLink = useCallback<InternalMarkdownLinkOpener>(
    async (href, baseFilePath, event) => {
      const base =
        baseFilePath && baseFilePath.length > 0
          ? baseFilePath
          : getActiveTabBasePath()
      const hrefToResolve = hrefForInAppDocumentNavigation(href) ?? href
      try {
        const resolved = await resolveDocumentLink(hrefToResolve, base)
        if (!resolved) {
          console.warn("[InternalLink] Could not resolve:", href)
          return
        }
        const targetId = resolved.filePath || resolved.documentId
        const synthetic = event
          ? ({
              metaKey: event.metaKey,
              ctrlKey: event.ctrlKey,
              preventDefault: () => {},
              stopPropagation: () => {},
            } as ReactMouseEvent)
          : undefined
        await navigateToDocument(targetId, synthetic)
      } catch (e) {
        console.error("[InternalLink] Navigation failed:", e)
      }
    },
    [navigateToDocument, getActiveTabBasePath]
  )

  return (
    <InternalMarkdownLinkContext.Provider value={openInternalMarkdownLink}>
      {children}
    </InternalMarkdownLinkContext.Provider>
  )
}

/**
 * Returns a handler for MarkdownPreview `onInternalLinkClick`, or undefined when
 * used outside InternalMarkdownLinkProvider (links keep default browser behavior).
 */
export function useInternalMarkdownLinkClick(
  baseFilePathOverride?: string | null
) {
  const open = useContext(InternalMarkdownLinkContext)
  return useMemo(() => {
    if (!open) return undefined
    return (href: string, event?: MouseEvent) => {
      const base =
        baseFilePathOverride != null && baseFilePathOverride.length > 0
          ? baseFilePathOverride
          : undefined
      void open(href, base, event)
    }
  }, [open, baseFilePathOverride])
}
